/**
 * W22-1 — URL → store hydration for site routing.
 *
 * Symptom: cold-loading `/sites/{siteId}?tab=…` rendered the Dashboard
 * (or the last-opened report) instead of the requested site. Root cause
 * was that nothing in the router chain pushed the URL's `siteId` into
 * `siteRag.activeSiteId` or flipped `engineView` to `'site'`, so
 * SiteLandingPage never mounted.
 *
 * This hook restores the mapping. On mount and on every `popstate`,
 * it reads `window.location.pathname`, matches it against
 * `/sites/{id}` (and `/sites/{id}/reports/{id}` when a report is open),
 * and writes into the store ONLY when state DIFFERS from the URL.
 *
 * Idempotency (R1 mitigation): the effect only fires setters when state
 * DIFFERS from the URL. In-app navigation that's already pointed at the
 * same site/report is a no-op.
 *
 * In-app site switching (R3): when `activeSiteId` disagrees with the
 * URL's site id, bail so a sidebar click is not snapped back to a stale
 * `/sites/A` path before navigation runs. Re-run on `activeSiteId` +
 * `location.pathname` so once store and URL agree we still promote
 * `engineView` from `dashboard` → `site` (replaceState does not notify
 * React Router).
 *
 * Sites-not-loaded-yet race: if `fetchSites` hasn't returned when the
 * effect first runs, `sites` is empty and the hook bails. The dep array
 * includes `sites` / `reports`, so the effect re-runs once data lands.
 *
 * Unknown siteId: the hook bails without touching state, so the
 * Dashboard renders. The downstream `SiteLandingPage.tsx` `?tab=`
 * reader still works because it reads URL params directly off
 * `window.location.search`.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  parseSiteAppPath,
  syncBrowserUrlForActiveSite,
  type ParsedSiteAppPath,
} from '../../lib/siteNavigation';

export type EngineView = 'dashboard' | 'site' | 'comparison' | 'report';

export interface SiteIdHolder {
  id: string;
}

export interface ReportIdHolder {
  id: string;
  siteId?: string;
}

/** @deprecated Import from `lib/siteNavigation` — re-exported for W22-1 tests. */
export { extractSiteIdFromPath } from '../../lib/siteNavigation';

/**
 * Apply hydration once. Pure: takes inputs + setters, returns nothing.
 */
export function hydrateSiteragFromUrl(args: {
  pathname: string;
  sites: ReadonlyArray<SiteIdHolder>;
  reports: ReadonlyArray<ReportIdHolder>;
  activeSiteId: string | null;
  activeReportId: string | null;
  engineView: EngineView;
  setActiveSite: (id: string | null) => void;
  setActiveRagReport: (id: string | null) => void;
  setEngineView: (v: EngineView) => void;
  /** True for browser back/forward — URL wins over in-app selection. */
  fromPopstate?: boolean;
  /**
   * True when this run is the FIRST hydration of the current pathname (an
   * explicit navigation just landed, or a cold load). A fresh URL wins over a
   * pre-set `activeReportId` — a store default (`loadReports` picking
   * `reports[0]`) or a previously-open report must not shadow the report the
   * URL names. Re-runs on the SAME pathname (store-dep changes) keep the
   * store-wins guard so an in-app switch isn't clobbered by a stale URL.
   */
  urlIsFresh?: boolean;
}): boolean {
  const {
    pathname,
    sites,
    reports,
    activeSiteId,
    activeReportId,
    engineView,
    setActiveSite,
    setActiveRagReport,
    setEngineView,
    fromPopstate = false,
    urlIsFresh = false,
  } = args;

  const parsed = parseSiteAppPath(pathname);
  const { siteId, reportId, isRerun } = parsed;
  if (!siteId) return true;

  const siteExists = sites.some((s) => s.id === siteId);
  // Data not loaded yet — report "not handled" so the pathname stays fresh
  // and the deferred re-run (sites/reports arriving) can still apply the URL.
  if (!siteExists) return false;

  if (!fromPopstate && !urlIsFresh && activeSiteId !== null && activeSiteId !== siteId) return true;

  const promoteReportView = () => {
    if (engineView !== 'report') setEngineView('report');
  };
  const promoteSiteView = () => {
    if (engineView === 'dashboard') setEngineView('site');
    else if (fromPopstate && engineView !== 'site' && engineView !== 'report') {
      setEngineView('site');
    } else if (fromPopstate && !reportId && engineView !== 'site') {
      setEngineView('site');
    }
  };

  // Report reader URL (rerun panel hydration is owned by SiteRagReportView).
  if (reportId && !isRerun) {
    const reportExists = reports.some((r) => r.id === reportId && (!r.siteId || r.siteId === siteId));
    if (!reportExists) return false;
    // Store-wins guard for RE-RUNS on an already-handled pathname only. On a
    // FRESH pathname the URL is authoritative — without this, any pre-set
    // activeReportId (loadReports defaulting to reports[0], or a previously
    // open report) silently rendered a DIFFERENT report than the URL named.
    if (!fromPopstate && !urlIsFresh && activeReportId !== null && activeReportId !== reportId) {
      return true;
    }
    if (activeSiteId !== siteId) setActiveSite(siteId);
    if (activeSiteId !== siteId || activeReportId !== reportId) setActiveRagReport(reportId);
    promoteReportView();
    return true;
  }

  // Site landing (includes `/rerun` — keep report open; panel opens separately).
  if (activeSiteId !== siteId) setActiveSite(siteId);
  if (!isRerun && activeReportId !== null) setActiveRagReport(null);
  if (isRerun) {
    const reportExists = reportId && reports.some((r) => r.id === reportId);
    if (reportExists && activeReportId !== reportId) setActiveRagReport(reportId);
    if (reportExists) promoteReportView();
    else promoteSiteView();
    return true;
  }
  // Reports-list / site-landing URL is AUTHORITATIVE (NAV-LIST fix): a path
  // with no `/reports/:id` segment must render the LIST, never a report.
  // Besides clearing `activeReportId` (above), demote a stale `engineView ===
  // 'report'` back to 'site'. This closes the auto-jump bug where
  // `loadReports` defaults `activeReportId = reports[0].id` (a report the user
  // never opened) and the SiteRagEngine engineView-derivation effect snaps to
  // the report view as soon as that report is found for the active site.
  // A genuinely-open report ALWAYS carries the `/reports/:id` segment, so it
  // is handled by the report branch above and never reaches this demotion.
  if (engineView === 'report') {
    setEngineView('site');
    return true;
  }
  promoteSiteView();
  return true;
}

/** @deprecated Use `hydrateSiteragFromUrl` — site-only shim for older tests. */
export function hydrateActiveSiteFromUrl(args: {
  pathname: string;
  sites: ReadonlyArray<SiteIdHolder>;
  activeSiteId: string | null;
  engineView: EngineView;
  setActiveSite: (id: string | null) => void;
  setEngineView: (v: EngineView) => void;
  fromPopstate?: boolean;
}): void {
  hydrateSiteragFromUrl({
    ...args,
    reports: [],
    activeReportId: null,
    setActiveRagReport: () => {},
  });
}

export { syncBrowserUrlForActiveSite };

/**
 * React hook wrapper — hydrates on mount + on `popstate`. Caller passes
 * the relevant store slices + actions.
 */
export function useUrlSiteHydration(args: {
  sites: ReadonlyArray<SiteIdHolder>;
  reports: ReadonlyArray<ReportIdHolder>;
  activeSiteId: string | null;
  activeReportId: string | null;
  engineView: EngineView;
  setActiveSite: (id: string | null) => void;
  setActiveRagReport: (id: string | null) => void;
  setEngineView: (v: EngineView) => void;
}): void {
  const {
    sites,
    reports,
    activeSiteId,
    activeReportId,
    engineView,
    setActiveSite,
    setActiveRagReport,
    setEngineView,
  } = args;
  const location = useLocation();
  const activeSiteIdRef = useRef(activeSiteId);
  const activeReportIdRef = useRef(activeReportId);
  const engineViewRef = useRef(engineView);
  activeSiteIdRef.current = activeSiteId;
  activeReportIdRef.current = activeReportId;
  engineViewRef.current = engineView;
  // Last pathname this hook has HANDLED (hydration got past the data-loading
  // bails). A pathname not seen here yet is a fresh navigation — the URL wins
  // over a pre-set activeReportId. Not-handled paths stay fresh so deferred
  // sites/reports arrivals still apply the URL on the re-run.
  const lastHandledPathRef = useRef<string | null>(null);
  const lastActiveSiteIdRef = useRef<string | null>(activeSiteId);

  useEffect(() => {
    const hydrate = (fromPopstate = false) => {
      const pathname = window.location.pathname;
      const parsedPath = parseSiteAppPath(pathname);
      const inAppSiteSwitch =
        !fromPopstate &&
        activeSiteIdRef.current !== lastActiveSiteIdRef.current &&
        activeSiteIdRef.current !== null &&
        parsedPath.siteId !== null &&
        parsedPath.siteId !== activeSiteIdRef.current;
      lastActiveSiteIdRef.current = activeSiteIdRef.current;
      if (inAppSiteSwitch) {
        lastHandledPathRef.current = pathname;
      }
      const handled = hydrateSiteragFromUrl({
        pathname,
        sites,
        reports,
        activeSiteId: activeSiteIdRef.current,
        activeReportId: activeReportIdRef.current,
        engineView: engineViewRef.current,
        setActiveSite,
        setActiveRagReport,
        setEngineView,
        fromPopstate,
        urlIsFresh: !inAppSiteSwitch && lastHandledPathRef.current !== pathname,
      });
      if (handled) lastHandledPathRef.current = pathname;
    };
    hydrate(false);
    const onPopstate = () => hydrate(true);
    window.addEventListener('popstate', onPopstate);
    return () => window.removeEventListener('popstate', onPopstate);
  }, [
    sites,
    reports,
    setActiveSite,
    setActiveRagReport,
    setEngineView,
    location.pathname,
    activeSiteId,
    activeReportId,
    // NAV-LIST: re-run when engineView changes so a stale 'report' view that
    // the SiteRagEngine derivation effect forces (without an `activeReportId`
    // or pathname change) is still demoted to 'site' when the URL has no
    // `/reports/:id` segment. Idempotent: the pure core only fires setters
    // when state disagrees with the URL.
    engineView,
  ]);
}

export type { ParsedSiteAppPath };
